<template>
    <div class="forum-home-top">
        <ForumThreadList
            ref="topThreadList"
            class="top-thread-list"
            :sortable="true"
            :sort-order="sortOrder"
            :threads="topThreads"
            @update:sort-order="$emit('update:sort-order', $event)"
        />

        <div
            v-if="!canLoadMoreTopThreads"
            class="between-threads-msg">
            <em v-if="topThreads.length === 0">
                No top threads this {{ period }}.
            </em>
            <em v-else>
                End of top threads for this {{ period }}.
            </em>
        </div>

        <ForumThreadList
            v-if="!canLoadMoreTopThreads"
            ref="otherThreadList"
            class="other-thread-list"
            :header="false"
            :sort-order="sortOrder"
            :threads="otherThreads"
        />

        <Loader
            v-if="canLoadMoreThreads"
            v-show="loadingThreads"
        />

        <div
            v-if="!canLoadMoreThreads"
            class="after-threads-msg">
            That's all, folks!
        </div>
    </div>
</template>

<script>
    import ForumUtils      from '../../utils/forum';
    import ForumThreadList from '../reusable/forum/thread-list';
    import Loader          from '../reusable/forum/loader';

    function getViewportBottom() {
        const $window = $(window);
        return $window.scrollTop() + $window.height();
    }

    function getBottomPos(elem) {
        const $elem = $(elem);
        return $elem.offset().top + $elem.outerHeight();
    }

    export default {
        props: {
            sortOrder: {
                type: String,
                default: 'default',
            },

            period: {
                type: String,
                default: 'week',
                validator(value) {
                    return [
                        'week',
                        'month',
                        'semester',
                    ].includes(value);
                },
            },
        },

        components: {
            ForumThreadList,
            Loader,
        },

        data() {
            return {
                topThreadList: new ForumUtils.PaginatedThreadList(),
                otherThreadList: new ForumUtils.PaginatedThreadList(),
            };
        },

        async mounted() {
            window.addEventListener('scroll', this.autoLoadThreads);
            window.addEventListener('resize', this.autoLoadThreads);

            // Reload the thread lists rather than auto loading since the
            // original thread lists are created without parameters.
            await this.reloadThreadLists();
        },

        beforeDestroy() {
            window.removeEventListener('scroll', this.autoLoadThreads);
            window.removeEventListener('resize', this.autoLoadThreads);
        },

        methods: {
            isThreadListBottomNearView(el) {
                const offset = 200;
                const viewBottom = getViewportBottom();
                const listBottom = getBottomPos(el);
                return listBottom <= viewBottom + offset;
            },

            shouldLoadMoreTopThreads() {
                if (!this.canLoadMoreTopThreads) {
                    // There is nothing else to load.
                    return false;

                } else if (this.topThreadList.isLoading()) {
                    // We are already loading the next batch.
                    return false;

                } else if (this.topThreads.length === 0) {
                    // If the thread list was just reloaded, the bottom may
                    // still be out of view due to the old rendered threads; in
                    // this case, we want to fetch the new ones.
                    return true;
                }

                const el = this.$refs.topThreadList.$el;
                return this.isThreadListBottomNearView(el);
            },

            shouldLoadMoreLatestThreads() {
                if (!this.canLoadMoreLatestThreads) {
                    // There is nothing else to load.
                    return false;

                } else if (this.otherThreadList.isLoading()) {
                    // We are already loading the next batch.
                    return false;

                } else if (this.otherThreads.length === 0) {
                    // If the thread list was just reloaded, the bottom may
                    // still be out of view due to the old rendered threads; in
                    // this case, we want to fetch the new ones.
                    return true;
                }

                const el = this.$refs.otherThreadList.$el;
                return this.isThreadListBottomNearView(el);
            },

            async autoLoadThreads() {
                const autoLoadTop = async () => {
                    while (this.shouldLoadMoreTopThreads()) {
                        await this.topThreadList.loadMore();
                    }
                };
                const autoLoadLatest = async () => {
                    while (this.shouldLoadMoreLatestThreads()) {
                        await this.otherThreadList.loadMore();
                    }
                };
                await Promise.all([
                    autoLoadTop(),
                    autoLoadLatest(),
                ]);
            },

            async reloadThreadLists() {
                const tp = this.topSearchParams;
                this.topThreadList = new ForumUtils.PaginatedThreadList(tp);

                const lp = this.otherSearchParams;
                this.otherThreadList = new ForumUtils.PaginatedThreadList(lp);

                await this.autoLoadThreads();
            },
        },

        computed: {
            topSearchParams() {
                let order = this.sortOrder;
                let period = this.period;

                if (order === 'default') {
                    order = 'likeCount:desc';
                }

                return { order, period };
            },

            otherSearchParams() {
                let order = this.sortOrder;
                let notPeriod = this.period;

                if (order === 'default') {
                    order = 'lastPostCreatedAt:desc';
                }

                return { order, notPeriod };
            },

            topThreads() {
                return this.topThreadList.threads;
            },

            otherThreads() {
                return this.otherThreadList.threads;
            },

            canLoadMoreTopThreads() {
                return this.topThreadList.hasMore();
            },

            canLoadMoreLatestThreads() {
                return this.otherThreadList.hasMore();
            },

            canLoadMoreThreads() {
                return this.canLoadMoreTopThreads ||
                       this.canLoadMoreLatestThreads;
            },

            loadingThreads() {
                return this.topThreadList.isLoading() ||
                       this.otherThreadList.isLoading();
            },
        },

        watch: {
            topSearchParams() {
                this.reloadThreadLists();
            },

            otherSearchParams() {
                this.reloadThreadLists();
            },
        },
    }
</script>

<style lang="scss" scoped>
    .top-thread-list {
        margin-top: 20px;
    }

    .between-threads-msg {
        opacity: 0.6;
        padding: 15px 10px;
    }

    .after-threads-msg {
        font-weight: bold;
        margin-top: 30px;
    }
</style>



// WEBPACK FOOTER //
// ForumHomeTop.vue?a49a2f22